import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { formatDate } from '../functions/formatDate';
import { IDepartment } from '../interfaces/department/IDepartment';
import { IUser } from '../interfaces/user/IUser';
import { darkTheme, lightTheme } from '../mui/theme';

export const useDepartmentColumns = (
  setSelectedItem: (vacation: IDepartment) => void,
  setShowDeleteModal: (show: boolean) => void,
): GridColDef[] => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const navigate = useNavigate();
  const iconFill =
    userData?.theme === ETheme.LIGHT
      ? lightTheme.palette.text.primary
      : darkTheme.palette.text.primary;
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'name',
      headerName: t('departments.department'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => <div>{params?.row?.name}</div>,
    },
    {
      field: 'users.displayName',
      headerName: t('header.employees'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div>
          {params?.row?.users?.length ? params?.row?.users
            ?.map((user: IUser) => user?.displayName)
            ?.join(', ') : '-'}
        </div>
      ),
    },
    {
      field: 'updatedAt',
      headerName: t('general.updatedAt'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { updatedAt } = params?.row;
        return <div>{formatDate(updatedAt)}</div>;
      },
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 150,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {/* Edit or View Icon */}
            <Tooltip title={t('general.edit')}>
              <span
                className={'d-flex'}
                onClick={() => {
                  navigate(
                    `/${isNormalUser ? 'user' : 'admin'}/departments/${params?.row?.id}`,
                  );
                }}
              >
                {isNormalUser ? (
                  <RemoveRedEyeOutlinedIcon fill={iconFill} />
                ) : (
                  <EditIcon fill={iconFill} />
                )}
              </span>
            </Tooltip>

            {/* Delete Icon */}
            {!isNormalUser && (
              <Tooltip title={t('general.delete')}>
                <span
                  className={'ms-3 d-flex'}
                  onClick={() => {
                    setSelectedItem(params?.row);
                    setShowDeleteModal(true);
                  }}
                >
                  <TrashIcon fill={iconFill} />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
};
